import type { BillingStatus, EntitlementFeature } from '../context/AuthContext';

/**
 * Server-decided feature access from `/billing/status` `entitlements`
 * (backend `entitlements.can`). `fallback` is used only when the payload
 * doesn't carry the feature (older backend, or status not loaded yet).
 */
export function hasEntitlement(
  status: BillingStatus | null | undefined,
  feature: EntitlementFeature,
  fallback: boolean,
): boolean {
  const value = status?.entitlements?.[feature];
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * Whether an analysis can count toward ICF progress. Chat and Library used to
 * derive this differently (CHAT-22); both read this helper now.
 */
export function canTrackIcfProgress(
  status: BillingStatus | null | undefined,
  options: { isSuperAdmin: boolean; isSchoolMember: boolean },
): boolean {
  const tier = (status?.plan_tier || '').toLowerCase();
  const tier2Plus = tier === 'tier2' || tier === 'tier3';
  const subscriptionActive = ['active', 'trialing'].includes(
    (status?.subscription_status || '').toLowerCase(),
  );
  const fallback = options.isSuperAdmin
    || (tier2Plus && (subscriptionActive || options.isSchoolMember));
  return hasEntitlement(status, 'icf_progress', fallback);
}

export function hasEffectiveBillingAccess(
  status: BillingStatus | null | undefined,
  options: { requireBoundedB2CTrial: boolean },
): boolean {
  // The server's paywall decision wins when present (includes the D10
  // past_due grace, course access and the bounded app trial).
  const serverDecision = status?.entitlements?.paid_access;
  if (typeof serverDecision === 'boolean') return serverDecision;
  const subscriptionStatus = (status?.subscription_status || '').toLowerCase();
  if (subscriptionStatus === 'active') return true;
  // School-course participants: time-boxed access, mirrors has_course_access on the server.
  if (status?.course_access_active) return true;
  if (subscriptionStatus === 'trialing') {
    return options.requireBoundedB2CTrial
      ? Boolean(status?.free_trial_active)
      : true;
  }
  return Boolean(status?.free_trial_active);
}

const MENTOR_USER_TYPES = new Set(['b2c', 'b2b2c_mentor', 'b2b2c_admin']);

/**
 * Whether Mentees (`/mentor/engagements`) is open: the server's
 * `mentor_engagements` entitlement (tier3 only). Without the payload (older
 * backend) it falls back to tier3 plus real plan access; purchased credits
 * never count.
 */
export function canOpenMentorEngagements(
  status: BillingStatus | null | undefined,
  options: { userType: string | null | undefined; requireBoundedB2CTrial: boolean },
): boolean {
  const fallback = (status?.plan_tier || '').toLowerCase() === 'tier3'
    && MENTOR_USER_TYPES.has(options.userType || '')
    && hasEffectiveBillingAccess(status, { requireBoundedB2CTrial: options.requireBoundedB2CTrial });
  return hasEntitlement(status, 'mentor_engagements', fallback);
}

/**
 * D3: the subscription has lapsed but purchased credits keep analyses, own
 * reports and credit purchase open. Only the server payload can say so; with
 * no payload (older backend) this is false and routing keeps its old rule.
 */
export function hasCreditOnlyAccess(status: BillingStatus | null | undefined): boolean {
  const entitlements = status?.entitlements;
  if (!entitlements || entitlements.paid_access !== false) return false;
  return entitlements.analyze === true || entitlements.view_own_reports === true;
}

/**
 * How the chat composer is gated (D3, founder decision 2026-10-09):
 * - `open`: chat is allowed.
 * - `reassess_only`: a lapsed subscriber with purchased credits, in a session
 *   with a linked transcript. The server bills a target-level re-assessment
 *   or re-analysis one credit (`chat_reassess`) and refuses plain chat with
 *   402, so the composer stays usable with re-assessment copy.
 * - `locked`: no chat at all.
 * Without the payload `chat` stays open and `chat_reassess` closed, so an
 * older backend keeps its previous behaviour.
 */
export type ChatLockMode = 'open' | 'reassess_only' | 'locked';

export function chatLockMode(
  status: BillingStatus | null | undefined,
  options: { sessionHasTranscript: boolean },
): ChatLockMode {
  if (hasEntitlement(status, 'chat', true)) return 'open';
  return options.sessionHasTranscript && hasEntitlement(status, 'chat_reassess', false)
    ? 'reassess_only'
    : 'locked';
}

/**
 * D10: past_due while Stripe retries the charge, and the server still grants
 * paid access. Without the payload this is false (past_due -> recovery).
 */
export function isInDunningGrace(status: BillingStatus | null | undefined): boolean {
  return (status?.subscription_status || '').toLowerCase() === 'past_due'
    && status?.entitlements?.paid_access === true;
}

/**
 * The "last payment failed" banner: personal (B2C) accounts in D10 grace.
 * School members are excluded; their billing belongs to the school admin and
 * the personal Stripe portal can't fix it.
 */
export function shouldShowPaymentFailedBanner(status: BillingStatus | null | undefined): boolean {
  return isInDunningGrace(status)
    && (status?.plan_type || '').toLowerCase() !== 'b2b2c';
}

export function shouldStartCardBackedTrial(
  status: BillingStatus | null | undefined,
): boolean {
  return Boolean(
    status?.free_trial_eligible
    && !status?.free_trial_active
    && !status?.free_trial_expired,
  );
}
